
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type UserRole = 'customer' | 'studio_owner' | 'staff' | 'admin';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  userRole: UserRole | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string, role?: UserRole) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  isAuthenticated: boolean;
  isStudioOwner: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  isCustomer: boolean;
  supabase: typeof supabase; // Expose supabase client
  createTestAccount?: (role: UserRole, identifier?: string) => Promise<{email: string, password: string}>;
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Check for active session on mount
    const fetchSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        setSession(data.session);
        setUser(data.session?.user || null);
        
        if (data.session?.user) {
          const role = data.session.user.user_metadata?.role as UserRole || null;
          setUserRole(role);
        }
      } catch (error: any) {
        console.error('Error fetching session:', error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth state changed:', event);
        setSession(currentSession);
        setUser(currentSession?.user || null);
        
        if (currentSession?.user) {
          const role = currentSession.user.user_metadata?.role as UserRole || null;
          setUserRole(role);
        } else {
          setUserRole(null);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Erfolgreich angemeldet",
        description: "Du wurdest erfolgreich eingeloggt.",
      });
    } catch (error: any) {
      toast({
        title: "Anmeldung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithOtp({ 
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        } 
      });
      
      if (error) {
        throw error;
      }
      
      return toast({
        title: "Magic Link gesendet",
        description: "Überprüfe deine E-Mail für den Login-Link.",
      });
    } catch (error: any) {
      toast({
        title: "Magic Link konnte nicht gesendet werden",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        throw error;
      }
      
      // Note: No toast here as we'll be redirected to Google
    } catch (error: any) {
      toast({
        title: "Google-Anmeldung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, role: UserRole = 'customer') => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Registrierung erfolgreich",
        description: "Dein Konto wurde erstellt. Überprüfe deine E-Mails für die Bestätigung.",
      });
    } catch (error: any) {
      toast({
        title: "Registrierung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/change-password`,
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Passwort-Reset Link gesendet",
        description: "Überprüfe deine E-Mails für den Passwort-Reset Link.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler beim Zurücksetzen des Passworts",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.updateUser({ 
        password: newPassword 
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Passwort aktualisiert",
        description: "Dein Passwort wurde erfolgreich aktualisiert.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler beim Aktualisieren des Passworts",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Erfolgreich abgemeldet",
        description: "Du wurdest erfolgreich ausgeloggt.",
      });
    } catch (error: any) {
      toast({
        title: "Abmeldung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to create test accounts (only in development)
  const createTestAccount = async (role: UserRole, identifier: string = Date.now().toString()) => {
    // Only allow this in development environment
    if (process.env.NODE_ENV !== 'development' && !window.location.hostname.includes('localhost')) {
      throw new Error('Test accounts can only be created in development environment');
    }
    
    const testEmail = `test-${role}-${identifier}@chrona.example.com`;
    const testPassword = 'Test1234!';
    
    try {
      const { error } = await supabase.auth.admin.createUser({
        email: testEmail,
        password: testPassword,
        email_confirm: true,
        user_metadata: {
          role
        }
      });
      
      if (error) throw error;
      
      return { email: testEmail, password: testPassword };
    } catch (error: any) {
      console.error('Error creating test account:', error);
      toast({
        title: 'Fehler beim Erstellen des Testkontos',
        description: error.message,
        variant: 'destructive'
      });
      throw error;
    }
  };

  const isAuthenticated = !!session;
  const isStudioOwner = userRole === 'studio_owner';
  const isStaff = userRole === 'staff';
  const isAdmin = userRole === 'admin';
  const isCustomer = userRole === 'customer';

  const value = {
    session,
    user,
    userRole,
    isLoading,
    signIn,
    signInWithMagicLink,
    signInWithGoogle,
    signUp,
    resetPassword,
    updatePassword,
    signOut,
    isAuthenticated,
    isStudioOwner,
    isStaff,
    isAdmin,
    isCustomer,
    supabase, // Expose supabase client
    createTestAccount // Only included in dev
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
